import type { FramePresentationEvidence, StreamRecoveryEvidence } from '../streamRecovery/contracts';
import type { NetworkPathObserver, SelectedPathReader } from '../streamRecovery/interfaces';
import type { DecodeHintCollector, RvfcVideoElement } from '../decodeHintCollector';
import type { GuestStatsSample } from '../guestStreamStatsSampler';
import type { PresentationQualityTracker } from '../streamQuality/presentationQualityTracker';
import type { VisibilitySource } from '../guestSessionLifecycle';
import { NetworkChangeSubscription, type ChangeEventSource } from '../networkHandoff/NetworkChangeSubscription';
import { FRAME_EVIDENCE_STALE_MS } from '../streamRecovery/policy';
import { DecoderObservationTracker } from '../presentationRecovery/DecoderObservationTracker';

/** Session-scoped composition of existing sample/frame producers; owns no polling loop. */
export class GuestStreamRecoveryBinding {
  private readonly events: NetworkChangeSubscription;
  private video: (RvfcVideoElement & { paused?: boolean; ended?: boolean }) | null = null;
  private active = false;
  private path: StreamRecoveryEvidence['path'];
  private readonly decoded: DecoderObservationTracker;
  private decoder: StreamRecoveryEvidence['decoder'];

  constructor(private readonly deps: {
    pathReader: SelectedPathReader; pathObserver: NetworkPathObserver;
    presentation: PresentationQualityTracker; collector: DecodeHintCollector;
    visibility?: VisibilitySource; isMobile?: boolean;
    eventSources: () => { ice?: ChangeEventSource; network?: ChangeEventSource };
    requestSample: () => void; onPathChanged: (epoch: number) => void;
    nowMs?: () => number;
  }) {
    this.events = new NetworkChangeSubscription({ requestSample: deps.requestSample });
    this.decoded = new DecoderObservationTracker({ nowMs: deps.nowMs ?? (() => performance.now()) });
  }

  attach(): void { this.active = true; this.events.attach(this.deps.eventSources()); }
  attachVideo(video: RvfcVideoElement | null): void { this.video = video; }

  /** Called before differencing this same report, so a changed route cannot import old baselines. */
  observeReport(report: RTCStatsReport): boolean {
    if (!this.active) return false;
    this.events.attach(this.deps.eventSources());
    const observation = this.deps.pathObserver.observe(this.deps.pathReader.read(report));
    this.path = observation.current;
    if (observation.changed) {
      this.decoded.reset();
      this.deps.onPathChanged(observation.current.epoch);
    }
    return observation.changed;
  }

  observeSample(sample: GuestStatsSample): void {
    this.decoded.observe(sample.decodeSample);
    const implementation = sample.wire.decoderImplementation;
    const powerEfficient = sample.wire.powerEfficientDecoder;
    this.decoder = { kind: 'unknown', ...(implementation ? { implementation } : {}),
      ...(powerEfficient !== undefined ? { powerEfficient } : {}) };
  }

  presentationEvidence(): FramePresentationEvidence {
    const playback = !this.video ? 'unknown' : this.video.ended ? 'ended' :
      this.video.paused === true ? 'paused' : this.video.paused === false ? 'playing' : 'unknown';
    return this.deps.presentation.snapshotEvidence({ visibility: this.deps.visibility?.current() ?? 'unknown',
      playback, ...this.decoded.snapshot(), playbackQuality: this.deps.collector.playbackQuality() });
  }

  evidence(): StreamRecoveryEvidence {
    const observation = this.deps.collector.peekLatestObservation();
    return { version: 1, path: this.path, presentation: this.presentationEvidence(), decoder: this.decoder,
      deviceClass: this.deps.isMobile === undefined ? 'unknown' : this.deps.isMobile ? 'mobile' : 'desktop',
      latency: observation ? { state: observation.observationAgeMs < FRAME_EVIDENCE_STALE_MS ? 'fresh' : 'stale',
        ageMs: observation.observationAgeMs, sequence: observation.observationSequence } : { state: 'unavailable' } };
  }

  resetForConnection(): void {
    this.deps.pathObserver.reset(); this.path = undefined; this.decoded.reset(); this.decoder = undefined;
    this.deps.collector.resetForPath();
  }

  dispose(): void { this.active = false; this.events.dispose(); }
}
